import { useMutation, useQuery } from "@tanstack/react-query"
import { AlertCircle } from "lucide-react"
import { useState } from "react"
import { api, ErrorApi } from "@/api/cliente"
import { Funciones } from "@/componentes/contratos/FuncionesDelContrato"
import { Insignia } from "@/componentes/ui/Insignia"
import { comoFecha } from "@/lib/fechas"

/**
 * Un contrato, con lo que se puede hacerle.
 *
 * ⚠ **Cada tarjeta pide SU vista previa, y sólo mientras está abierta.** Tenerla en
 * el padre obligaba a llevar allí cuál está desplegada, y el estado de una tarjeta
 * no es asunto de la lista. Con `enabled` no se pide nada hasta que alguien mira.
 *
 * ⚠ **El error se pinta aquí y no arriba.** Un fallo al emitir el contrato de 2024
 * anunciado en la cabecera de la sección no dice sobre cuál de los cinco fue.
 */

export type Contrato = {
  id: number
  cargo_id: number
  tipo: "DETERMINADO" | "INDETERMINADO" | "PASANTIA" | "HONORARIOS"
  inicio: string
  fin: string | null
  salario: string | null
  divisa: string | null
  estado: "BORRADOR" | "VIGENTE" | "TERMINADO" | "ANULADO"
  funciones: string[] | null
  nombre_cargo: string | null
  emitido_en: string | null
  motivo_cierre: string | null
}

const TONO = {
  BORRADOR: "neutro",
  VIGENTE: "exito",
  TERMINADO: "neutro",
  ANULADO: "peligro",
} as const

const TIPO = {
  DETERMINADO: "Determinado",
  INDETERMINADO: "Indeterminado",
  PASANTIA: "Pasantía",
  HONORARIOS: "Honorarios",
}

/** Lo que dice cada cierre. Terminado es el que se cumplió; anulado, el que nunca
 *  debió existir. Contarlos juntos haría que una liquidación sumara años que no
 *  fueron, así que tampoco se nombran igual aquí. */
const CIERRE = {
  terminar: {
    titulo: "Terminar el contrato",
    etiqueta: "Motivo del cierre",
    nota: "El contrato se cumplió y llegó a su fin. No se borra: queda en el expediente.",
  },
  anular: {
    titulo: "Anular el contrato",
    etiqueta: "¿Por qué se anula?",
    nota: "Anulado es el contrato que nunca debió existir. No cuenta como tiempo trabajado.",
  },
}

type Accion = "terminar" | "anular"

export function TarjetaContrato({
  contrato,
  onCambio,
}: {
  contrato: Contrato
  onCambio: () => void
}) {
  const [detalle, setDetalle] = useState(false)
  const [emitiendo, setEmitiendo] = useState(false)
  const [cerrando, setCerrando] = useState<Accion | null>(null)
  const [motivo, setMotivo] = useState("")

  const previa = useQuery({
    queryKey: ["contrato-previa", contrato.id],
    queryFn: () =>
      api.get<{ funciones: string[]; difiere: boolean }>(
        `/api/v1/contratos/${contrato.id}/vista-previa`,
      ),
    enabled: detalle,
  })

  const emitir = useMutation({
    mutationFn: () => api.post(`/api/v1/contratos/${contrato.id}/emitir`),
    onSuccess: () => {
      setEmitiendo(false)
      onCambio()
    },
  })

  const cerrar = useMutation({
    mutationFn: (accion: Accion) =>
      api.post(`/api/v1/contratos/${contrato.id}/${accion}`, { motivo }),
    onSuccess: () => {
      setCerrando(null)
      setMotivo("")
      onCambio()
    },
  })

  const error = emitir.error ?? cerrar.error

  function abrirCierre(accion: Accion) {
    setCerrando(accion)
    setMotivo("")
    cerrar.reset()
  }

  return (
    <li data-testid={`contrato-${contrato.id}`} className="superficie p-3">
      <div className="flex flex-wrap items-center gap-2">
        <Insignia tono={TONO[contrato.estado]}>{contrato.estado}</Insignia>
        {/* El nombre congelado manda sobre el del catálogo: renombrar el puesto no
            cambia lo que alguien firmó. */}
        <span className="text-sm font-medium">{contrato.nombre_cargo ?? "Cargo actual"}</span>
        <span className="text-xs" style={{ color: "var(--fg-muted)" }}>
          {TIPO[contrato.tipo]}
        </span>
        <span className="text-xs tabular-nums" style={{ color: "var(--fg-muted)" }}>
          {comoFecha(contrato.inicio)} → {contrato.fin ? comoFecha(contrato.fin) : "indefinido"}
        </span>
        {contrato.salario !== null && (
          <span className="text-xs tabular-nums" style={{ color: "var(--fg-muted)" }}>
            {contrato.salario} {contrato.divisa}
          </span>
        )}
      </div>

      {contrato.motivo_cierre !== null && (
        <p className="mt-1 text-xs" style={{ color: "var(--fg-muted)" }}>
          {contrato.motivo_cierre}
        </p>
      )}

      {error && (
        <p
          role="alert"
          className="mt-2 flex items-start gap-2 text-sm"
          style={{ color: "var(--destructive)" }}
        >
          <AlertCircle size={16} aria-hidden={true} />
          {error instanceof ErrorApi ? error.message : "No se pudo completar la operación"}
        </p>
      )}

      <div className="mt-2 flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => setDetalle(!detalle)}
          data-testid={`funciones-${contrato.id}`}
          className="boton-secundario h-7 px-2 text-xs"
        >
          {detalle ? "Ocultar funciones" : "Ver funciones"}
        </button>
        {contrato.estado === "BORRADOR" && (
          <button
            type="button"
            onClick={() => setEmitiendo(true)}
            data-testid={`emitir-${contrato.id}`}
            className="boton-primario h-7 px-2 text-xs"
          >
            Emitir
          </button>
        )}
        {contrato.estado === "VIGENTE" && (
          <button
            type="button"
            onClick={() => abrirCierre("terminar")}
            data-testid={`terminar-${contrato.id}`}
            className="boton-secundario h-7 px-2 text-xs"
          >
            Terminar
          </button>
        )}
        {(contrato.estado === "BORRADOR" || contrato.estado === "VIGENTE") && (
          <button
            type="button"
            onClick={() => abrirCierre("anular")}
            data-testid={`anular-${contrato.id}`}
            className="boton-secundario h-7 px-2 text-xs"
            style={{ color: "var(--destructive)" }}
          >
            Anular
          </button>
        )}
      </div>

      {emitiendo && (
        <div className="mt-3 superficie p-3" style={{ borderColor: "var(--primary-borde)" }}>
          <p className="text-sm">
            Al emitirlo, las funciones del cargo quedan <strong>congeladas</strong>: lo que se
            cambie después en Procesos o en el catálogo no tocará este contrato.{" "}
            <strong>No se puede volver a borrador.</strong> Si sale mal, habrá que anularlo y hacer
            otro.
          </p>
          <div className="mt-3 flex gap-2">
            <button
              type="button"
              onClick={() => setEmitiendo(false)}
              className="boton-secundario h-8 px-3 text-xs"
            >
              Cancelar
            </button>
            <button
              type="button"
              disabled={emitir.isPending}
              onClick={() => emitir.mutate()}
              data-testid={`confirmar-emision-${contrato.id}`}
              className="boton-primario h-8 px-3 text-xs"
            >
              {emitir.isPending ? "Emitiendo…" : "Emitir y congelar"}
            </button>
          </div>
        </div>
      )}

      {cerrando !== null && (
        <div className="mt-3 superficie p-3">
          {/* ⚠ El motivo es obligatorio: un cierre sin motivo es una fila que dice
              que algo acabó y no dice nada más. */}
          <label
            htmlFor={`motivo-${contrato.id}`}
            className="mb-1 block text-xs font-medium uppercase"
          >
            {CIERRE[cerrando].etiqueta}
          </label>
          <input
            id={`motivo-${contrato.id}`}
            className="campo"
            required={true}
            minLength={3}
            maxLength={200}
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
          />
          <p className="mt-2 text-xs" style={{ color: "var(--fg-muted)" }}>
            {CIERRE[cerrando].nota}
          </p>
          <div className="mt-3 flex gap-2">
            <button
              type="button"
              onClick={() => setCerrando(null)}
              className="boton-secundario h-8 px-3 text-xs"
            >
              Cancelar
            </button>
            <button
              type="button"
              disabled={motivo.trim().length < 3 || cerrar.isPending}
              onClick={() => cerrar.mutate(cerrando)}
              data-testid={`confirmar-${cerrando}-${contrato.id}`}
              className="boton-primario h-8 px-3 text-xs"
            >
              {CIERRE[cerrando].titulo}
            </button>
          </div>
        </div>
      )}

      {detalle && (
        <Funciones
          contrato={contrato}
          previa={previa.data}
          cargando={previa.isPending}
          fallo={previa.isError}
        />
      )}
    </li>
  )
}
